import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { axiosInstance } from '../../shared/api/axiosInstance';
import { createQuickPost } from '../../shared/api/textProjectApi';

export interface StripeProductAttributes {
    name: string;
    active: boolean;
    description: string | null;
    metadata: Record<string, any>;
    features: string[];
    currency: string;
    unit_amount: number;
    type: string;
    recurring: {
        interval: string;
        interval_count: number;
        meter: string | null;
        trial_period_days: number | null;
        usage_type: string;
    };
}

export interface StripeProduct {
    type: 'stripe-product';
    id: string;
    attributes: StripeProductAttributes;
}

export interface UserSubscription {
    plan: string;
    status: string;
    tier: number;
    has_subscription: boolean;
    subscription_ends_at: string | null;
    auto_renew: boolean;
    is_on_trial: boolean;
    trial_ends_at: string | null;
    is_payment_failed: boolean;
    image_generations_used: number;
    image_generations_quota: number;
    remaining_images: number;
    direct_posts_used: number;
    direct_posts_quota: number;
    remaining_posts: number;
    social_account_used: number;
    social_account_quota: number;
    remaining_accounts: number;
    is_active: boolean;
    is_expired: boolean;
    is_trial_expired: boolean;
}

export interface SubscriptionState {
    products: StripeProduct[];
    userSubscription: UserSubscription | null;
    isLoading: boolean;
    error: string | null;
    checkoutResult: {
        type: 'checkout-session' | 'payment-redirect' | 'subscription-updated' | null;
        id?: string;
        url?: string;
        reason?: string;
        status?: string;
        product?: string;
        public_key?: string;
    } | null;
    portalUrl: string | null;
    isFreeSubscription: boolean;
    isBasicSubscription: boolean;
    isPremiumSubscription: boolean;
    isProSubscription: boolean;
}

export const initialState: SubscriptionState = {
    products: [],
    userSubscription: null,
    isLoading: false,
    error: null,
    checkoutResult: null,
    portalUrl: null,
    isFreeSubscription: false,
    isBasicSubscription: false,
    isPremiumSubscription: false,
    isProSubscription: false,
};

export const fetchSubscriptionProducts = createAsyncThunk<
    StripeProduct[],
    void,
    { rejectValue: string }
>('subscription/fetchProducts', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get('/subscription/products');

        const products: StripeProduct[] = response.data.data.map((p: any) => ({
            ...p,
            attributes: {
                ...p.attributes,
                features: (p.attributes.features || []).map((f: any) =>
                    typeof f === 'string' ? f : f?.name,
                ),
            },
        }));

        return products;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            return rejectWithValue(
                (error.response.data as any)?.message || 'Failed to fetch subscription products',
            );
        }
        return rejectWithValue('Unexpected error while fetching subscription products');
    }
});

export const fetchUserSubscription = createAsyncThunk<
    UserSubscription,
    void,
    { rejectValue: string }
>('subscription/fetchUserSubscription', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get('/subscription');
        const attributes = response.data.data.attributes;
        return {
            plan: attributes.plan,
            status: attributes.status,
            tier: attributes.tier,
            has_subscription: attributes.has_subscription,
            subscription_ends_at: attributes.subscription_ends_at,
            auto_renew: attributes.auto_renew,
            is_on_trial: attributes.is_on_trial,
            trial_ends_at: attributes.trial_ends_at,
            is_payment_failed: attributes.is_payment_failed,
            image_generations_used: attributes.image_generations_used,
            is_active: attributes.is_active,
            image_generations_quota: attributes.image_generations_quota,
            remaining_images: attributes.remaining_images,
            direct_posts_used: attributes.direct_posts_used,
            direct_posts_quota: attributes.direct_posts_quota,
            remaining_posts: attributes.remaining_posts,
            social_account_used: attributes.social_account_used,
            social_account_quota: attributes.social_account_quota,
            remaining_accounts: attributes.remaining_accounts,
            is_expired: attributes.is_expired,
            is_trial_expired: attributes.is_trial_expired,
        };
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            return rejectWithValue(
                (error.response.data as any)?.errors?.[0]?.detail || 'Failed to fetch subscription',
            );
        }
        return rejectWithValue('Unexpected error while fetching subscription');
    }
});

export const createCheckout = createAsyncThunk<
    {
        type: 'checkout-session' | 'payment-redirect' | 'subscription-updated';
        id?: string;
        url?: string;
        reason?: string;
        status?: string;
        product?: string;
        public_key?: string;
    },
    { product: string; redirect_uri: string; auto_renew?: boolean },
    { rejectValue: string }
>(
    'subscription/createCheckout',
    async ({ product, redirect_uri, auto_renew }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post('/subscription/checkout', {
                data: {
                    type: 'checkout-intent',
                    attributes: { product, redirect_uri, auto_renew },
                },
            });

            const resData = response.data.data;

            if (resData.type === 'checkout-session') {
                return {
                    type: 'checkout-session',
                    id: resData.id,
                    public_key: resData.attributes?.public_key,
                    product: resData.attributes?.product,
                };
            }

            if (resData.type === 'payment-redirect') {
                return {
                    type: 'payment-redirect',
                    url: resData.attributes.redirect,
                    reason: resData.attributes.reason,
                };
            }

            if (resData.type === 'subscription-updated') {
                return {
                    type: 'subscription-updated',
                    id: resData.id,
                    status: resData.attributes.status,
                    product: resData.attributes.product,
                };
            }

            throw new Error('Unexpected checkout response');
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                const msg = (error.response.data as any)?.errors?.[0]?.detail || 'Checkout failed';
                return rejectWithValue(msg);
            }
            return rejectWithValue('Unexpected error while creating checkout');
        }
    },
);

export const createPortal = createAsyncThunk<
    string,
    { redirect_uri: string },
    { rejectValue: string }
>('subscription/createPortal', async ({ redirect_uri }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/subscription/portal', { redirect_uri });
        const url = response.data?.data?.attributes?.url;
        if (!url) throw new Error('Portal URL not returned');
        return url;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            const msg =
                (error.response.data as any)?.errors?.[0]?.detail ||
                'Failed to create portal session';
            return rejectWithValue(msg);
        }
        return rejectWithValue('Unexpected error while creating portal session');
    }
});

export const subscriptionSlice = createSlice({
    name: 'subscription',
    initialState,
    reducers: {
        resetSubscriptionState: (state) => {
            state.isLoading = false;
            state.error = null;
            state.checkoutResult = null;
            state.portalUrl = null;
            state.isFreeSubscription = false;
            state.isBasicSubscription = false;
            state.isPremiumSubscription = false;
            state.isProSubscription = false;
        },
        updateUserSubscription: (state, action) => {
            state.userSubscription = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSubscriptionProducts.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSubscriptionProducts.fulfilled, (state, action) => {
                state.isLoading = false;
                state.products = action.payload;
            })
            .addCase(fetchSubscriptionProducts.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to fetch products';
            })

            .addCase(fetchUserSubscription.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchUserSubscription.fulfilled, (state, action) => {
                console.log('fetchUserSubscription', action.payload);
                state.isLoading = false;
                state.userSubscription = action.payload;

                const plan = action.payload.plan ?? 'free';

                state.isFreeSubscription = plan === 'free';
                state.isBasicSubscription = plan === 'basic';
                state.isPremiumSubscription = plan === 'premium';
                state.isProSubscription = plan === 'pro';
            })
            .addCase(fetchUserSubscription.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to fetch subscription';
            })

            .addCase(createCheckout.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.checkoutResult = null;
            })
            .addCase(createCheckout.fulfilled, (state, action) => {
                state.isLoading = false;
                state.checkoutResult = action.payload;
            })
            .addCase(createCheckout.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Checkout failed';
            })

            .addCase(createPortal.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.portalUrl = null;
            })
            .addCase(createPortal.fulfilled, (state, action) => {
                state.isLoading = false;
                state.portalUrl = action.payload;
            })
            .addCase(createPortal.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Portal creation failed';
            });
    },
});

export const { resetSubscriptionState, updateUserSubscription } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
